'use client';

import * as React from 'react';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@lib/utils';

import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Button } from './button';

interface MonthPickerProps {
  value?: Date | string;
  onChange?: (date: Date | undefined) => void;
  className?: string;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function MonthPicker({ value, onChange, className }: MonthPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [internalDate, setInternalDate] = React.useState<Date | undefined>(
    value ? new Date(value) : undefined
  );

  const currentYear = internalDate
    ? internalDate.getFullYear()
    : new Date().getFullYear();

  const [displayYear, setDisplayYear] = React.useState(currentYear);

  React.useEffect(() => {
    setInternalDate(value ? new Date(value) : undefined);
    if (value) {
      const newDate = new Date(value);

      setDisplayYear(newDate.getFullYear());
    }
  }, [value]);

  const handleMonthSelect = (monthIndex: number) => {
    const newDate = new Date(displayYear, monthIndex, 1);

    setInternalDate(newDate);
    onChange?.(newDate);
    setOpen(false);
  };

  const handlePreviousYear = () => {
    setDisplayYear((prev) => prev - 1);
  };

  const handleNextYear = () => {
    setDisplayYear((prev) => prev + 1);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            'w-48 justify-between font-normal border-[#C6C6C6]',
            className
          )}
          id="month-picker"
          variant="outline"
        >
          {internalDate ? format(internalDate, 'MMMM yyyy') : 'Select month'}
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto overflow-hidden p-0 !bg-[#262626] border-[#3D3D3D]"
      >
        <div className="p-4">
          {/* Year selector */}
          <div className="flex items-center justify-between mb-4">
            <Button
              className="h-7 w-7 p-0"
              variant="ghost"
              onClick={handlePreviousYear}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <div className="text-sm font-medium text-white">{displayYear}</div>
            <Button
              className="h-7 w-7 p-0"
              variant="ghost"
              onClick={handleNextYear}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-3 gap-2">
            {MONTHS.map((month, index) => {
              const isSelected =
                internalDate &&
                internalDate.getMonth() === index &&
                internalDate.getFullYear() === displayYear;

              return (
                <Button
                  key={month}
                  className={cn(
                    'h-9 text-xs font-normal',
                    isSelected
                      ? 'bg-[#B6D72C] text-black hover:bg-[#B6D72C]/90'
                      : 'bg-transparent hover:bg-[#3D3D3D]'
                  )}
                  variant="ghost"
                  onClick={() => handleMonthSelect(index)}
                >
                  {month.substring(0, 3)}
                </Button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
